import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import PMSwitch from "../../../utils/PMSwitch";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { SystemSelector } from "../../../utils/moduleSelectors/systemSelector";
import { ContactsSelector } from "../../../utils/moduleSelectors/contactSelector";
import { DeliveryAddressSelector } from "../../../utils/moduleSelectors/deliveryAddressSelector";

export const AddSubDialog = ({ open, onClose, onSubmit, agreement }) => {
  const { t } = useTranslation();
  const [useCustomerAddress, setUseCustomerAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const subData = {
        ...values,
        use_customer_address: useCustomerAddress
      };
      
      await onSubmit(subData);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setSubmitError(error.message || "Failed to add sub");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      title="Add Sub"
      open={open}
      onClose={onClose}
      size="2xl"
    >
      <Formik
        initialValues={{
          name: "",
          customer_id: null,
          system_id: null,
          contact_id: null,
          delivery_address_id: null,
          use_customer_address: false,
        }}
        validationSchema={Yup.object({
          name: Yup.string(),
          customer_id: Yup.number().nullable(),
        })}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form>
            <div className="space-y-6">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <CustomerSelector
                    label="Customer"
                    name="customer_id"
                    selected={null}
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <PMField
                    name="name"
                    type="text"
                    label="Name/Title (Optional)"
                    placeholder="Enter name or title for this sub"
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <SystemSelector
                    label="System"
                    name="system_id"
                    module="CUSTOMER"
                    module_param={formik.values.customer_id}
                    selected={null}
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <ContactsSelector
                    label="Contact"
                    name="contact_id"
                    module_param={formik.values.customer_id}
                    selected={null}
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <div className="space-y-3">
                    <PMSwitch
                      name="use_customer_address"
                      label="Use Customer Address"
                      checked={useCustomerAddress}
                      onChange={(e) => {
                        setUseCustomerAddress(e.target.checked);
                        if (e.target.checked) {
                          formik.setFieldValue("delivery_address_id", null);
                        }
                      }}
                    />
                    
                    {!useCustomerAddress && (
                      <DeliveryAddressSelector
                        label="Delivery Address"
                        name="delivery_address_id"
                        module="CUSTOMER"
                        module_param={formik.values.customer_id}
                        selected={null}
                        {...formik}
                      />
                    )}
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Error
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{submitError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isSubmitting ? "Adding..." : "Add Sub"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
